/** @jsxImportSource @emotion/react */
import { Grid, Tooltip } from '@mui/material';
import dayjs from 'dayjs';
import { useDispatch } from 'react-redux';
import BackArrowIcon from '../../../../assets/backArrowIcon';
import BackDateIcon from '../../../../assets/backDateIcon';
import NextDateIcon from '../../../../assets/nextDateIcon';
import { MAX_TODAY_TIME } from '../../../../constants';
import { useTelegram } from '../../../../hooks/useTelegram';
import { setEditedUserSpace, setOfficeSpace } from '../../../../store/reducers/officeSpace';

import styles from './styles';

interface BookingDateProps {
    startDateState?: any;
    endDateState: string | null;
    editWorkPlaceActive: boolean;
    periodActive?: boolean;
    setActiveStep?: (step: number) => void;
    setValue?: (
        fieldName: 'startDate' | 'endDate',
        value: string,
        options?: { shouldDirty: boolean },
    ) => void;
}

function BookingDate({
    startDateState,
    endDateState,
    periodActive,
    setActiveStep,
    setValue,
    editWorkPlaceActive,
}: BookingDateProps) {
    const dispatch = useDispatch();

    const { tg, colorScheme } = useTelegram();

    const goNextDateHandler = () => {
        const nextDay = dayjs(startDateState).add(1, 'day').format('YYYY-MM-DD');
        setValue?.('startDate', nextDay, { shouldDirty: true });
        setValue?.('endDate', nextDay, { shouldDirty: true });
        dispatch(setOfficeSpace({ newSpace: '' }));
        dispatch(setEditedUserSpace({ newEditedUserSpace: null }));
    };

    const goPrevDateHandler = () => {
        const prevDay = dayjs(startDateState).subtract(1, 'day').format('YYYY-MM-DD');
        setValue?.('startDate', prevDay, { shouldDirty: true });
        setValue?.('endDate', prevDay, { shouldDirty: true });
        dispatch(setOfficeSpace({ newSpace: '' }));
        dispatch(setEditedUserSpace({ newEditedUserSpace: null }));
    };

    const startDateString = dayjs(startDateState).format('DD.MM.YYYY');
    const endDateString = dayjs(endDateState).format('DD.MM.YYYY');

    const today = dayjs();

    const todayHours = today.get('hour');

    if (todayHours > MAX_TODAY_TIME) {
        today.add(1, 'day');
    }

    const showBackBtn =
        dayjs(startDateState)
            .set('hours', 0)
            .set('minutes', 0)
            .set('milliseconds', 0)
            .isAfter(today.set('hours', 0).set('minutes', 0).set('milliseconds', 0)) &&
        !editWorkPlaceActive;

    const showNextBtn =
        dayjs(startDateState)
            .set('hours', 0)
            .set('minutes', 0)
            .set('milliseconds', 0)
            .isAfter(
                today.subtract(1, 'day').set('hours', 0).set('minutes', 0).set('milliseconds', 0),
            ) && !editWorkPlaceActive;

    const goBackHandler = () => {
        tg.MainButton.hide();
        dispatch(setOfficeSpace({ newSpace: '' }));
        setActiveStep?.(0);
    };

    return (
        <div>
            {periodActive ? (
                <div css={styles.chosenDateWrapperStyles({ colorScheme })}>
                    {!editWorkPlaceActive && (
                        <Tooltip
                            title="Back to the booking"
                            placement="top"
                            componentsProps={{
                                tooltip: {
                                    sx: {
                                        backgroundColor:
                                            colorScheme === 'dark' ? '#383B40' : '#ffffff',
                                        color: colorScheme === 'dark' ? '#ffffff' : '#000000',
                                        boxShadow: 'rgba(0, 0, 0, 0.35) 0px 5px 15px',
                                        fontWeight: 400,
                                        fontSize: '8px',
                                        bottom: '10px',
                                        left: '40px',
                                        position: 'relative',
                                    },
                                },
                                popper: {
                                    sx: {
                                        height: '10px',
                                    },
                                },
                            }}
                        >
                            <button
                                type="button"
                                css={styles.goBackBtnStyles({ colorScheme })}
                                onClick={goBackHandler}
                            >
                                <BackArrowIcon />
                            </button>
                        </Tooltip>
                    )}
                    <p css={styles.dateTextStyles({ colorScheme })}>
                        {startDateString} - {endDateString}
                    </p>
                </div>
            ) : (
                <Grid container position="relative" justifyContent="center" mb="5px">
                    <Grid item xs={12}>
                        <div css={styles.headerWrapperStyles({ colorScheme })}>
                            {showBackBtn && (
                                <button
                                    type="button"
                                    css={styles.backDateBtnStyles({ colorScheme })}
                                    onClick={goPrevDateHandler}
                                >
                                    <BackDateIcon />
                                </button>
                            )}
                            <div css={styles.headerInfoWrapperStyles}>
                                <p css={styles.chosenDateStyles}>{startDateString}</p>
                            </div>
                            {showNextBtn && (
                                <button
                                    type="button"
                                    css={styles.nextDateBtnStyles({ colorScheme })}
                                    onClick={goNextDateHandler}
                                >
                                    <NextDateIcon />
                                </button>
                            )}
                        </div>
                    </Grid>
                </Grid>
            )}
        </div>
    );
}

export default BookingDate;
